/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: types.ts
 🗓️ Created: 2026-05-02
 🧩 Description:
    Types for the HTTP/HTTPS Server module.
────────────────────────────────────────────────────────────────*/

export type TlsVersion = '1.0' | '1.1' | '1.2' | '1.3';
export type CipherProfile = 'modern' | 'intermediate' | 'legacy';
export type ErrorLogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * A CORS-allowed origin entry — one URL that is allowed to access this
 * Ring2All instance from a browser. These entries ONLY drive the CORS
 * allowed-origins list on the API server.
 *
 * SSL certificate assignment is handled exclusively in the
 * Telephony Domains module (domains.certificate_id).
 */
export interface CorsOriginEntry {
    url: string;  // Full URL e.g. "https://pbx.ring2all.com"
}

/** @deprecated Use CorsOriginEntry — kept for backwards-compat during migration */
export type PlatformDomain = CorsOriginEntry;

export interface HttpServerConfig {
    id: number;
    uuid: string;

    // Listeners
    httpPort: number;
    httpsPort: number;
    bindAddress: string;
    enableHttp: boolean;
    enableHttps: boolean;

    // HTTPS Policy
    forceHttps: boolean;
    tlsMinVersion: TlsVersion;
    tlsMaxVersion: TlsVersion;
    cipherProfile: CipherProfile;
    hstsEnabled: boolean;
    hstsMaxAge: number;
    hstsIncludeSubdomains: boolean;
    hstsPreload: boolean;

    // Default Certificate (global fallback — used when accessing by IP or unconfigured domain)
    defaultCertificateId?: number | null;

    // CORS Allowed Origins
    corsOrigins: CorsOriginEntry[];

    // Logging & Security
    accessLogEnabled: boolean;
    errorLogLevel: ErrorLogLevel;
    maxRequestSize: number;
    rateLimitEnabled: boolean;
    rateLimitRequests: number;
    rateLimitWindow: number;
    disableTlsRenegotiation: boolean;

    // Relations
    tenantId: number;

    // Audit
    createdBy?: number | null;
    updatedBy?: number | null;
    createdAt?: string;
    updatedAt?: string;
}

export interface HttpServerFormState {
    // Listeners
    httpPort: number;
    httpsPort: number;
    bindAddress: string;
    enableHttp: boolean;
    enableHttps: boolean;

    // HTTPS Policy
    forceHttps: boolean;
    tlsMinVersion: TlsVersion;
    tlsMaxVersion: TlsVersion;
    cipherProfile: CipherProfile;
    hstsEnabled: boolean;
    hstsMaxAge: number;
    hstsIncludeSubdomains: boolean;
    hstsPreload: boolean;

    // Default Certificate
    defaultCertificateId?: number | null;

    // CORS Allowed Origins
    corsOrigins: CorsOriginEntry[];

    // Logging & Security
    accessLogEnabled: boolean;
    errorLogLevel: ErrorLogLevel;
    maxRequestSize: number;
    rateLimitEnabled: boolean;
    rateLimitRequests: number;
    rateLimitWindow: number;
    disableTlsRenegotiation: boolean;
}

export const DEFAULT_HTTP_SERVER_CONFIG: HttpServerFormState = {
    httpPort: 80,
    httpsPort: 443,
    bindAddress: '0.0.0.0',
    enableHttp: true,
    enableHttps: true,
    forceHttps: false,
    tlsMinVersion: '1.2',
    tlsMaxVersion: '1.3',
    cipherProfile: 'modern',
    hstsEnabled: false,
    hstsMaxAge: 31536000,
    hstsIncludeSubdomains: false,
    hstsPreload: false,
    defaultCertificateId: null,
    corsOrigins: [],
    accessLogEnabled: true,
    errorLogLevel: 'error',
    maxRequestSize: 10485760,
    rateLimitEnabled: false,
    rateLimitRequests: 100,
    rateLimitWindow: 60,
    disableTlsRenegotiation: true
};
